import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

const starStyles = {
  container: {
    display: "flex",
    flexDirection: "row",
    gap: 8,
    margin: "20px 0",
    cursor: "pointer",
    justifyContent: "center",
  },
  star: {
    width: 40,
    height: 40,
    color: "#ddd",
    transition: "all 0.3s ease",
    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
  },
  starFilled: {
    color: "#d4af37",
    filter: "drop-shadow(0 4px 8px rgba(212, 175, 55, 0.3))",
  },
};

const styles = {
  form: {
    margin: "30px auto",
    padding: "40px 20px",
    borderRadius: "20px",
    maxWidth: "100%",
    width: "800px",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
    fontFamily: "'Cormorant Garamond', serif",
    position: "relative",
    overflow: "hidden",
  },

  heading: {
    marginBottom: 30,
    fontWeight: "600",
    fontSize: "2.2rem",
    color: "#2c1810",
    textAlign: "center",
    fontFamily: "'Cormorant Garamond', serif",
    letterSpacing: "1px",
    position: "relative",
  },

  textarea: {
    width: "100%",
    minHeight: 120,
    padding: "16px",
    fontSize: "1.1rem",
    borderRadius: "15px",
    border: "1px solid rgba(212, 175, 55, 0.3)",
    resize: "vertical",
    fontFamily: "'Cormorant Garamond', serif",
    marginTop: 15,
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    backdropFilter: "blur(10px)",
    transition: "all 0.3s ease",
    color: "#2c1810",
  },
  textareaFocus: {
    outline: "none",
    border: "2px solid #d4af37",
    boxShadow: "0 0 20px rgba(212, 175, 55, 0.2)",
  },
  error: {
    color: "#e74c3c",
    marginTop: 15,
    fontWeight: "500",
    textAlign: "center",
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "1rem",
    padding: "10px",
    borderRadius: "8px",
    backgroundColor: "rgba(231, 76, 60, 0.1)",
  },
  button: {
    marginTop: 25,
    width: "100%",
    padding: "16px 0",
    backgroundColor: "linear-gradient(135deg, #d4af37, #af8f4c)",
    color: "#fff",
    fontWeight: "600",
    fontSize: "1.2rem",
    border: "none",
    borderRadius: "15px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    fontFamily: "'Cormorant Garamond', serif",
    letterSpacing: "0.5px",
    position: "relative",
    overflow: "hidden",
  },
  buttonHover: {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 25px rgba(212, 175, 55, 0.3)",
  },
  buttonDisabled: {
    backgroundColor: "rgba(149, 165, 166, 0.6)",
    cursor: "not-allowed",
    transform: "none",
  },
  loginMessage: {
    textAlign: "center",
    fontSize: "1.1rem",
    fontFamily: "'Cormorant Garamond', serif",
    color: "#2c1810",
    lineHeight: "1.6",
  },
  loginButton: {
    color: "#d4af37",
    fontWeight: "600",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "1.1rem",
    fontFamily: "'Cormorant Garamond', serif",
    padding: "8px 16px",
    textDecoration: "underline",
    transition: "all 0.3s ease",
    borderRadius: "8px",
  },
  loginButtonHover: {
    backgroundColor: "rgba(212, 175, 55, 0.1)",
    textDecoration: "none",
  },
  loadingText: {
    textAlign: "center",
    fontSize: "1.1rem",
    fontFamily: "'Cormorant Garamond', serif",
    color: "#2c1810",
    fontStyle: "italic",
  },
};

// Composant étoile SVG (plein, vide, ou moitié plein)
const Star = ({ filled = 0 }) => {
  if (filled === 1) {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="#d4af37"
        stroke="#d4af37"
        strokeWidth="1"
        width="40"
        height="40"
      >
        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
      </svg>
    );
  } else if (filled === 0.5) {
    return (
      <svg viewBox="0 0 24 24" width="40" height="40">
        <defs>
          <linearGradient id="halfGrad">
            <stop offset="50%" stopColor="#d4af37" />
            <stop offset="50%" stopColor="#ddd" />
          </linearGradient>
        </defs>
        <path
          fill="url(#halfGrad)"
          stroke="#d4af37"
          strokeWidth="1"
          d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
        />
      </svg>
    );
  } else {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="#ddd"
        strokeWidth="1"
        width="40"
        height="40"
      >
        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
      </svg>
    );
  }
};

const AddAvis = ({ productId, onAvisAdded }) => {
  const [note, setNote] = useState(0);
  const [commentaire, setCommentaire] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hoverNote, setHoverNote] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [isHovered, setIsHovered] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Vérifie l'auth au montage
  useEffect(() => {
    axios
      .get(`${apiUrl}/api/auth/check`, { withCredentials: true })
      .then(() => setIsAuthenticated(true))
      .catch(() => setIsAuthenticated(false));
  }, []);

  const handleStarClick = (index, isHalf) => {
    const newNote = isHalf ? index + 0.5 : index + 1;
    setNote(newNote);
  };

  const renderStars = () => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      const val = hoverNote || note;
      let fill = 0;
      if (val >= i + 1) fill = 1;
      else if (val >= i + 0.5) fill = 0.5;

      stars.push(
        <div
          key={i}
          style={{ 
            position: "relative", 
            display: "inline-block",
            transition: "transform 0.3s ease"
          }}
          onMouseLeave={() => setHoverNote(0)}
          onMouseMove={(e) => {
            const box = e.currentTarget.getBoundingClientRect();
            const mouseX = e.clientX - box.left;
            setHoverNote(mouseX < box.width / 2 ? i + 0.5 : i + 1);
          }}
          onClick={(e) => {
            const box = e.currentTarget.getBoundingClientRect();
            const mouseX = e.clientX - box.left;
            handleStarClick(i, mouseX < box.width / 2);
          }}
          aria-label={`Donner ${i + 1} étoiles`}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleStarClick(i, false);
            }
          }}
        >
          <Star filled={fill} />
        </div>
      );
    }
    return stars;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (note < 0.5 || note > 5) {
      setError("La note doit être entre 0.5 et 5");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${apiUrl}/api/avis`,
        { productId, note, commentaire },
        { withCredentials: true }
      );
      setNote(0);
      setCommentaire("");
      if (onAvisAdded) onAvisAdded(response.data);
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'envoi de l'avis");
    } finally {
      setLoading(false);
    }
  };

  if (isAuthenticated === null) {
    return <p style={styles.loadingText}>Chargement de l'espace d'avis...</p>;
  }

  if (!isAuthenticated) {
    return (
      <div style={styles.form}>
        <p style={styles.loginMessage}>
          <strong>Vous devez être connecté pour laisser un avis.</strong>
        </p>
        <div style={{ marginTop: 20, textAlign: "center" }}>
          <button
            onClick={() => navigate("/login", { state: { from: location.pathname } })}
            style={styles.loginButton}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      <h3 style={styles.heading}>
        Laisser un avis
      </h3>

      <div style={starStyles.container} aria-label="Note par étoiles">
        {renderStars()}
      </div>

      <textarea
        value={commentaire}
        onChange={(e) => setCommentaire(e.target.value)}
        placeholder="Votre commentaire..."
        style={styles.textarea}
        rows={5}
        onFocus={(e) => {
          e.target.style.outline = "none";
          e.target.style.border = "2px solid #d4af37";
          e.target.style.boxShadow = "0 0 20px rgba(212, 175, 55, 0.2)";
        }}
        onBlur={(e) => {
          e.target.style.border = "1px solid rgba(212, 175, 55, 0.3)";
          e.target.style.boxShadow = "none";
        }}
      />

      {error && <p style={styles.error}>{error}</p>}

      <button
        type="submit"
        disabled={loading}
        style={{
          ...styles.button,
          ...(loading ? styles.buttonDisabled : {}),
          background: "linear-gradient(135deg, #d4af37, #af8f4c)",
        }}
        onMouseEnter={(e) => {
          if (!loading) {
            e.target.style.transform = "translateY(-2px)";
            e.target.style.boxShadow = "0 8px 25px rgba(212, 175, 55, 0.3)";
          }
        }}
        onMouseLeave={(e) => {
          if (!loading) {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "none";
          }
        }}
      >
        {loading ? "Envoi..." : "Envoyer l'avis"}
      </button>
    </form>
  );
};

export default AddAvis;
